/**
 * Sentry initialization for error tracking and monitoring
 * Configured for offline-first PWA with proper handling of:
 * - IndexedDB operations
 * - ServiceWorker events
 * - Offline queue management
 * - Network failures
 */

import * as Sentry from '@sentry/react'

export function initSentry() {
  // Only initialize in production
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE
  const dsn = import.meta.env.VITE_SENTRY_DSN
  
  if (environment !== 'production') {
    console.log('[Sentry] Skipping initialization in non-production environment:', environment)
    return
  }
  
  if (!dsn) {
    console.log('[Sentry] DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn,
    environment,
    
    // Release tracking (set via build process)
    release: import.meta.env.VITE_APP_VERSION,
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Track route changes
        enableInp: true,
      }),
      Sentry.replayIntegration({
        // Session replay for error debugging
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance sampling
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Downgrade expected errors to warnings
    beforeSend(event, hint) {
      const error = hint.originalException
      
      // Log network errors during offline mode as warnings
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message).toLowerCase()
        if (message.includes('failed to fetch') || 
            message.includes('network error') ||
            message.includes('networkerror')) {
          event.level = 'warning'
          event.tags = { ...event.tags, expected: 'network-offline' }
          return event
        }
      }
      
      // Log quota exceeded errors as warnings (IndexedDB full)
      if (error && typeof error === 'object' && 'name' in error) {
        if (error.name === 'QuotaExceededError') {
          event.level = 'warning'
          event.tags = { ...event.tags, expected: 'quota-exceeded' }
          return event
        }
      }
      
      return event
    },
    
    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors (handled by beforeSend)
      'NetworkError',
      'Failed to fetch',
      // ResizeObserver errors (non-critical)
      'ResizeObserver loop limit exceeded',
    ],
    
    // User context (set after authentication)
    initialScope: {
      tags: {
        app: 'ordelio-pwa',
      },
    },
  })

  console.log(`[Sentry] Initialized for ${environment}`)
}

/**
 * Set user context after authentication
 */
export function setSentryUser(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email,
  })
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null)
}

